import { useQuery } from "@tanstack/react-query";
import { Fragment } from "react";
import { Check } from "lucide-react";
import { ErrorState, PageHeader, Panel, Skeleton } from "@hostdock/ui";
import { api } from "../api";
import { RequirePermission } from "../lib/guard";
import { groupPermissions, usePermissionCatalogue } from "./roles";

export function PermissionsPage() {
  return (
    <RequirePermission anyOf={["SECURITY_MANAGE"]}>
      <PermissionMatrix />
    </RequirePermission>
  );
}

function PermissionMatrix() {
  const catalogue = usePermissionCatalogue();
  const roles = useQuery({ queryKey: ["roles"], queryFn: api.roles.list });
  const error = catalogue.error ?? roles.error;
  if (error) return <ErrorState error={error} onRetry={() => { void catalogue.refetch(); void roles.refetch(); }} />;
  const loading = catalogue.isPending || roles.isPending;
  return (
    <>
      <PageHeader title="Permissions" description="What each role can do. Permissions are enforced by the API on every request." />
      <Panel flush>
        {loading ? (
          <div className="p-4"><Skeleton className="h-64 w-full" /></div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full border-collapse text-[13px]">
              <thead>
                <tr className="border-b border-line bg-surface-2/60">
                  <th scope="col" className="sticky left-0 bg-surface-2 px-4 py-2 text-left text-[11.5px] font-semibold tracking-wide text-muted uppercase">Permission</th>
                  {roles.data!.items.map((r) => (
                    <th key={r.id} scope="col" className="px-3 py-2 text-center text-[12px] font-semibold whitespace-nowrap">{r.name}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {groupPermissions(catalogue.data!.items).map(([group, perms]) => (
                  <Fragment key={group}>
                    <tr className="border-b border-line">
                      <td colSpan={roles.data!.items.length + 1} className="bg-surface px-4 pt-3 pb-1 text-[11px] font-semibold tracking-wider text-subtle uppercase">{group}</td>
                    </tr>
                    {perms.map((p) => (
                      <tr key={p.key} className="border-b border-line last:border-0">
                        <td className="sticky left-0 bg-surface px-4 py-2">
                          <p className="font-mono text-[12px]">{p.key}</p>
                          <p className="text-[12px] text-muted">{p.description}</p>
                        </td>
                        {roles.data!.items.map((r) => (
                          <td key={r.id} className="px-3 py-2 text-center">
                            {r.permissions.includes(p.key) ? <Check size={15} className="mx-auto text-success" aria-label="Granted" /> : <span className="text-subtle" aria-label="Not granted">·</span>}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </Fragment>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Panel>
    </>
  );
}
